import { Fragment, useId, useMemo, useRef, useState } from 'react';
import type { ColumnProfile } from '@/lib/api';
import type { OntologySchema } from '@/lib/useOntology';
import {
  acceptSuggestion,
  acceptSuggestions,
  alignNameColumns,
  answered,
  applyKind,
  applyShape,
  cellChoice,
  columnKind,
  columnLetter,
  datatypeTerm,
  datatypeWords,
  KIND_TERMS,
  KIND_WORDS,
  ANNOTATION_PROPERTIES,
  mappingProblems,
  suggestedClassName,
  type ColumnKind,
  type ColumnMapping,
  type KeyMatch,
  type MintStrategy,
} from '@/lib/columnMapping';
import { cn } from '@/lib/utils';
import { Message } from '@/components/ui/Message';
import { Button } from '@/components/ui/Button';
import { AssocMenu, CreateClassDialog, Field, PickList, ReplacePairsEditor } from '@/components/import/mappingControls';
import { inputCls, termLabel } from '@/components/import/mappingStyle';

/**
 * Page 1 of the spreadsheet mapping (ruled 2026-09-30, 4.10): what each
 * column IS. An Entity column's cells are UUIDs, or labels a UUID is minted
 * for, of a class chosen here; a DatatypeProperty column's cells are
 * literals; an AnnotationProperty column's cells are strings about an
 * entity; a Rule column is defined on page 3. The properties themselves are
 * page 2. The sheet stays in view above; the row under the pointer names the
 * column it concerns through `onFocus`.
 */

const KINDS: { value: Exclude<ColumnKind, 'carried'>; help: string }[] = [
  { value: 'entity', help: 'Each cell names a thing of one class: a player, a game, a product. Its name, or its identifier.' },
  { value: 'datatype', help: 'Each cell is a value about a thing in another column: a date, a score, a price.' },
  { value: 'annotation', help: 'Each cell is a note or a name for a thing in another column: its name, a comment, a definition.' },
  { value: 'rule', help: 'Each cell is a code, such as Y, that stands for a fact the sheet does not write out. Defined on page 3.' },
  { value: 'skip', help: 'Nothing is read from this column.' },
];

const KEY_MATCH_OPTIONS: { value: KeyMatch; label: string; help: string }[] = [
  { value: 'mint', label: 'Always make a new thing', help: 'Every distinct cell becomes a new thing (minted).' },
  {
    value: 'match_or_create',
    label: 'Use the stored thing with this name, else make a new one',
    help: 'A cell that names a thing already in the store, by its name or another name it has (label or alternative label), is that thing; the rest are made new (minted).',
  },
  {
    value: 'match_only',
    label: 'Use only things already stored with this name',
    help: 'A cell that names nothing in the store, by its name or another name it has (label or alternative label), is left out.',
  },
];

const MINT_OPTIONS: { value: MintStrategy; label: string }[] = [
  { value: 'from_column', label: 'The cell alone' },
  { value: 'composite', label: 'The cell combined with other columns' },
  { value: 'surrogate', label: 'The whole row' },
];

/** A suggestion in plain words, and in the ontology's terms beneath. */
function describeSuggestion(s: ColumnMapping): { words: string; term: string | null } {
  const kind = columnKind(s);
  if (kind === 'skip') return { words: 'Skip: the column is empty', term: null };
  if (kind === 'entity') {
    const klass = s.klassLabel.trim() || s.klass.trim().replace(/^:/, '');
    const uuid = cellChoice(s) === 'uuid';
    return {
      words: `${KIND_WORDS.entity}: ${klass || 'a class to choose'}, ${uuid ? 'each cell its identifier' : 'each cell its name'}`,
      term: `Entity of ${s.klass || 'a class to choose'}, ${uuid ? 'the cell is the UUID' : 'a UUID is minted per label'}`,
    };
  }
  if (kind === 'datatype') {
    return {
      words: `${KIND_WORDS.datatype}: ${datatypeWords(s.valueType).toLowerCase()}`,
      term: `DatatypeProperty (${datatypeTerm(s.valueType)})`,
    };
  }
  if (kind === 'annotation') {
    const note = ANNOTATION_PROPERTIES.find((a) => a.predicate === s.predicate.trim())?.label ?? s.predicate.trim();
    return { words: `${KIND_WORDS.annotation}: ${note.toLowerCase()}`, term: `AnnotationProperty (${s.predicate.trim()})` };
  }
  return { words: KIND_WORDS[kind], term: KIND_TERMS[kind] };
}

/** An answer in plain words with its ontology term beside it, muted. */
function KindLabel({ kind }: { kind: ColumnKind }) {
  const term = KIND_TERMS[kind];
  return (
    <>
      {KIND_WORDS[kind]}
      {term && <span className="text-muted-foreground"> {term}</span>}
    </>
  );
}

const toggle = (s: Set<string>, k: string) => {
  const n = new Set(s);
  if (n.has(k)) n.delete(k);
  else n.add(k);
  return n;
};

export function ColumnsPage({
  mappings,
  profiles,
  schema,
  onChange,
  onFocus,
  onCreateClass,
  heading = true,
}: {
  mappings: ColumnMapping[];
  profiles: Map<string, ColumnProfile>;
  schema?: OntologySchema | null;
  onChange: (next: ColumnMapping[]) => void;
  /** The columns the person is deciding about now, for the sheet panel. */
  onFocus: (columns: string[]) => void;
  /** Off when the page's own heading is written above, over several sheets. */
  heading?: boolean;
  /** Declares a class in the ontology at once (ruled 2026-09-30) and answers
   *  with its URI, or with what refused it. */
  onCreateClass: (c: { label: string; parent: string; uri: string }) => Promise<{ ok: true; uri: string } | { ok: false; error: string }>;
}) {
  // Radio groups are named per page instance: several sheets on one screen
  // each render this page, and one name across them is one group to the
  // browser, which then shows a single choice ticked for the whole folder.
  const group = useId();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [placeholdersOpen, setPlaceholdersOpen] = useState<Set<string>>(new Set());
  const [creatingClass, setCreatingClass] = useState<number | null>(null);
  const [classRefused, setClassRefused] = useState<Map<string, string>>(new Map());
  const problems = useMemo(() => mappingProblems(mappings), [mappings]);
  // The mapping as it stands when a class the server was asked for answers,
  // so the answer lands on the current state and not the dialog's.
  const latest = useRef(mappings);
  latest.current = mappings;

  const changed = (idx: number, next: ColumnMapping) =>
    onChange(alignNameColumns(mappings, mappings.map((m, i) => (i === idx ? answered(next) : m)), idx));
  const set = (idx: number, patch: Partial<ColumnMapping>) => {
    const m = mappings[idx];
    if (m) changed(idx, { ...m, ...patch });
  };
  const put = (idx: number, next: ColumnMapping) => changed(idx, next);
  const accepted = (s: ColumnMapping) => acceptSuggestion(mappings, s);
  const acceptAll = () => onChange(acceptSuggestions(mappings));

  const waiting = mappings.filter((m) => m.unanswered).length;
  // Classes created in this mapping, listed until the ontology's own listing
  // has caught up with them.
  const createdHere = useMemo(() => {
    const seen = new Map<string, { klass: string; label: string; parent: string }>();
    for (const x of mappings) {
      const k = x.klass.trim();
      if (k && x.klassParent.trim() && !seen.has(k) && !schema?.classes.some((c) => c.uri === k)) {
        seen.set(k, { klass: k, label: x.klassLabel.trim() || k.replace(/^:/, ''), parent: x.klassParent.trim() });
      }
    }
    return [...seen.values()];
  }, [mappings, schema]);
  const sheetProblem = problems.find((p) => p.column === null && !p.text.includes('not answered'));
  const otherColumns = (column: string) => mappings.filter((x) => x.column !== column).map((x) => x.column);

  return (
    <div className="space-y-3">
      <datalist id="tres-onto-classes">
        {(schema?.classes ?? []).map((c) => (
          <option key={c.uri} value={c.uri}>
            {c.label ?? ''}
          </option>
        ))}
      </datalist>
      {heading && (
        <div>
          <h3 className="text-sm font-semibold">Page 1 — Columns: what each column is</h3>
          <p className="text-xs text-muted-foreground">
            Say what each column holds: a thing in a list (a player, a game), a fact about a thing (a date, a
            score), a note or name, or a code that stands for a fact. Each row starts with a suggestion read from
            your sheet, and each answer shows its ontology term beside it in grey. Facts and links are named on
            page 2, codes on page 3.
          </p>
        </div>
      )}

      {waiting > 0 ? (
        <Message kind="guidance" density="compact">
          {waiting} column{waiting === 1 ? ' is' : 's are'} not answered. Each row starts with a suggestion: press
          Accept, or Accept all suggestions, or answer it yourself. A suggestion does nothing until then.
        </Message>
      ) : (
        sheetProblem && (
          <Message kind="warning" density="compact">
            {sheetProblem.text}
          </Message>
        )
      )}
      {mappings.some((m) => m.unanswered && m.suggestion) && (
        <Button type="button" variant="secondary" size="sm" onClick={acceptAll}>
          Accept all suggestions
        </Button>
      )}

      <div className="overflow-x-auto">
        <table className="tres-table [&_td]:px-2 [&_th]:px-2">
          <thead>
            <tr>
              <th>Actions</th>
              <th>Column</th>
              <th>What it is</th>
              <th>Thing</th>
              <th>Adds to the ontology</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((m, idx) => {
              const info = profiles.get(m.column);
              const kind = m.unanswered ? null : columnKind(m);
              const isEntity = kind === 'entity' && m.job === 'id';
              const cell = cellChoice(m);
              const classProblem = problems.find((p) => p.column === m.column && p.field === 'class');
              const refused = classRefused.get(m.column);
              const placeholders = m.blankTokens.length > 0 || placeholdersOpen.has(m.column);
              const showDetail = expanded.has(m.column) || (placeholders && kind !== null && kind !== 'skip');
              const focus = () => onFocus([m.column]);
              return (
                <Fragment key={m.column}>
                  <tr onMouseEnter={focus} onFocus={focus} aria-label={`Column ${m.column}`}>
                    <td className="w-28 text-xs xl:w-52">
                      {m.unanswered && (
                        <div className="space-y-1">
                          {m.suggestion ? (
                            <>
                              <p className="font-medium text-tres-text-primary">
                                Suggested: {describeSuggestion(m.suggestion).words}
                              </p>
                              {describeSuggestion(m.suggestion).term && (
                                <p className="text-muted-foreground">{describeSuggestion(m.suggestion).term}</p>
                              )}
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                aria-label={`Accept the suggestion for ${m.column}`}
                                onClick={() => m.suggestion && put(idx, accepted(m.suggestion))}
                              >
                                Accept
                              </Button>
                            </>
                          ) : (
                            <p className="text-muted-foreground">Not answered yet. Say what the column is.</p>
                          )}
                        </div>
                      )}
                      {kind !== null && kind !== 'skip' && kind !== 'carried' && (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          aria-expanded={expanded.has(m.column)}
                          onClick={() => setExpanded((s) => toggle(s, m.column))}
                          className="whitespace-nowrap"
                        >
                          {expanded.has(m.column) ? 'Fewer options' : 'More options'}
                        </Button>
                      )}
                    </td>
                    <td className="max-w-40">
                      <div className="break-words font-mono text-xs">
                        {m.column} <span className="font-sans text-muted-foreground">({columnLetter(idx)})</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {(info?.samples ?? []).slice(0, 2).map((s, i) => (
                          <div key={i} className="max-w-40 truncate" title={s}>
                            {s}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      <fieldset aria-label={`What ${m.column} is`} className="space-y-0.5 whitespace-nowrap text-xs">
                        {kind === 'carried' && (
                          <label className="flex items-center gap-1.5">
                            <input type="radio" name={`${group}-kind-${idx}`} checked readOnly />
                            <KindLabel kind="carried" />
                          </label>
                        )}
                        {KINDS.map((o) => (
                          <label key={o.value} className="flex items-center gap-1.5" title={o.help}>
                            <input
                              type="radio"
                              name={`${group}-kind-${idx}`}
                              checked={kind === o.value}
                              onChange={() => put(idx, applyKind(m, o.value))}
                            />
                            <KindLabel kind={o.value} />
                          </label>
                        ))}
                      </fieldset>
                    </td>
                    <td className="min-w-44">
                      {isEntity && (
                        <div className="space-y-1">
                          <AssocMenu
                            label="Class"
                            value={m.klass.trim()}
                            answered={Boolean(m.klass.trim())}
                            invalid={Boolean(classProblem)}
                          >
                            {(close) => (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCreatingClass(idx);
                                    close();
                                  }}
                                  className="w-full rounded px-1 py-0.5 text-left font-medium hover:bg-muted"
                                >
                                  Create a class…
                                </button>
                                {m.klass.trim() && (
                                  <div className="mb-2 border-b border-border pb-2">
                                    <p className="px-1">
                                      <span className="font-medium">Chosen: </span>
                                      {termLabel(m.klass.trim(), schema?.classes.find((c) => c.uri === m.klass.trim())?.label)}
                                    </p>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        set(idx, { klass: '', klassParent: '', klassLabel: '' });
                                        close();
                                      }}
                                      className="w-full rounded px-1 py-0.5 text-left text-muted-foreground hover:bg-muted"
                                    >
                                      Clear the class
                                    </button>
                                  </div>
                                )}
                                {createdHere.filter((c) => c.klass !== m.klass.trim()).length > 0 && (
                                  <PickList
                                    title="Created in this mapping"
                                    items={createdHere.filter((c) => c.klass !== m.klass.trim())}
                                    keyOf={(c) => c.klass}
                                    labelOf={(c) => `${c.label} (${c.klass}, under ${c.parent})`}
                                    empty=""
                                    onPick={(c) => {
                                      set(idx, { klass: c.klass, klassParent: c.parent, klassLabel: c.label });
                                      close();
                                    }}
                                  />
                                )}
                                <PickList
                                  title="Classes in the ontology"
                                  items={schema?.classes ?? []}
                                  keyOf={(c) => c.uri}
                                  labelOf={(c) => termLabel(c.uri, c.label)}
                                  empty="The ontology declares no classes."
                                  onPick={(c) => {
                                    // A class created in this mapping keeps its parent, so the
                                    // mapping still declares it after its first column changes.
                                    const made = mappings.find((x) => x.klass.trim() === c.uri && x.klassParent.trim());
                                    set(idx, {
                                      klass: c.uri,
                                      klassParent: made?.klassParent ?? '',
                                      klassLabel: made?.klassLabel ?? '',
                                    });
                                    close();
                                  }}
                                />
                              </>
                            )}
                          </AssocMenu>
                          <fieldset aria-label={`What the ${m.column} cells are`} className="space-y-0.5 whitespace-nowrap text-xs">
                            <label className="flex items-center gap-1.5" title="Each cell is a name. TReS gives each distinct name an identifier (a UUID) and keeps the name as its :label.">
                              <input
                                type="radio"
                                name={`${group}-cell-${idx}`}
                                checked={cell === 'label'}
                                onChange={() => put(idx, applyShape(m, 'relationship', 'label'))}
                              />
                              Its name <span className="text-muted-foreground">label; UUID minted</span>
                            </label>
                            <label className="flex items-center gap-1.5" title="Each cell is the thing's identifier, used as the sheet has it, edited by the pattern under More options if needed.">
                              <input
                                type="radio"
                                name={`${group}-cell-${idx}`}
                                checked={cell === 'uuid'}
                                onChange={() => put(idx, applyShape(m, 'relationship', 'uuid'))}
                              />
                              Its identifier <span className="text-muted-foreground">UUID as written</span>
                            </label>
                          </fieldset>
                          {classProblem && !m.unanswered && (
                            <Message kind="warning" density="compact">
                              {classProblem.text}
                            </Message>
                          )}
                          {refused && (
                            <Message kind="warning" density="compact">
                              {refused}
                            </Message>
                          )}
                        </div>
                      )}
                      {kind === 'entity' && m.job === 'code' && (
                        <p className="text-xs text-muted-foreground">
                          Each cell is a term of {m.klass || 'a class'}, matched by its code or another name it has (notation or alternative label).
                        </p>
                      )}
                    </td>
                    <td className="font-mono text-xs">
                      {isEntity && m.klass.trim() && `?${m.column} :type ${m.klass.trim()}`}
                      {kind === 'datatype' && <span className="font-sans text-muted-foreground">A fact, named on page 2.</span>}
                      {kind === 'annotation' && <span className="font-sans text-muted-foreground">A note or name, chosen on page 2.</span>}
                      {kind === 'rule' && <span className="font-sans text-muted-foreground">A code, defined on page 3.</span>}
                    </td>
                  </tr>
                  {showDetail && kind !== null && kind !== 'skip' && (
                    <tr>
                      <td colSpan={5}>
                        <div className="flex flex-wrap gap-3">
                          <Field label="Placeholders that make no statement" className="min-w-48 flex-1">
                            <input
                              type="text"
                              value={m.blankTokens.join(',')}
                              placeholder="N/A, TBD, 0000"
                              onChange={(e) => {
                                setPlaceholdersOpen((s) => new Set(s).add(m.column));
                                set(idx, { blankTokens: e.target.value.split(',') });
                              }}
                              className={inputCls}
                            />
                          </Field>
                          <label className="flex min-w-48 flex-1 items-end gap-1.5 pb-1 text-xs text-muted-foreground">
                            <input
                              type="checkbox"
                              checked={m.carryDown}
                              onChange={(e) => set(idx, { carryDown: e.target.checked })}
                            />
                            A blank cell repeats the cell above
                          </label>
                          {isEntity && (
                            <Field label="When a name is already in the store" className="min-w-48 flex-1">
                              <select
                                aria-label={`Resolution of ${m.column}`}
                                value={m.keyMatch}
                                onChange={(e) => set(idx, { keyMatch: e.target.value as KeyMatch })}
                                className={inputCls}
                              >
                                {KEY_MATCH_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value} title={o.help}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                              <span className="mt-0.5 block text-xs text-muted-foreground">
                                {KEY_MATCH_OPTIONS.find((o) => o.value === m.keyMatch)?.help}
                              </span>
                            </Field>
                          )}
                          {isEntity && cell === 'uuid' && (
                            <>
                              <Field label="The identifier is made from" className="min-w-48 flex-1">
                                <select
                                  value={m.idStrategy === 'generated' ? 'from_column' : m.idStrategy}
                                  onChange={(e) => set(idx, { idStrategy: e.target.value as MintStrategy })}
                                  className={inputCls}
                                >
                                  {MINT_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                                {m.idStrategy === 'composite' && (
                                  <select
                                    multiple
                                    aria-label="Columns to combine"
                                    value={m.idAlso}
                                    onChange={(e) =>
                                      set(idx, { idAlso: Array.from(e.target.selectedOptions).map((o) => o.value) })
                                    }
                                    className={cn(inputCls, 'mt-1 h-24')}
                                  >
                                    {otherColumns(m.column).map((c) => (
                                      <option key={c} value={c}>
                                        {c}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </Field>
                              <Field label="Edit the cell by pattern (regular expression, replacement)" className="min-w-64 flex-1">
                                <div className="flex gap-1">
                                  <input
                                    type="text"
                                    aria-label={`Pattern for ${m.column}`}
                                    value={m.regexPattern}
                                    placeholder="\\."
                                    onChange={(e) => set(idx, { regexPattern: e.target.value })}
                                    className={cn(inputCls, 'font-mono')}
                                  />
                                  <input
                                    type="text"
                                    aria-label={`Replacement for ${m.column}`}
                                    value={m.regexReplacement}
                                    placeholder="-"
                                    onChange={(e) => set(idx, { regexReplacement: e.target.value })}
                                    className={cn(inputCls, 'font-mono')}
                                  />
                                </div>
                              </Field>
                              <label className="flex min-w-48 flex-1 items-end gap-1.5 pb-1 text-xs text-muted-foreground">
                                <input
                                  type="checkbox"
                                  checked={m.isLabel}
                                  onChange={(e) => set(idx, { isLabel: e.target.checked })}
                                />
                                The identifier is also its name (:label)
                              </label>
                            </>
                          )}
                          {(isEntity || kind === 'datatype' || kind === 'annotation') && (
                            <details className="min-w-48 flex-1">
                              <summary className="cursor-pointer text-xs text-muted-foreground">Replace text in the cell</summary>
                              <ReplacePairsEditor
                                pairs={m.replacePairs}
                                onChange={(replacePairs) => set(idx, { replacePairs })}
                              />
                            </details>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {creatingClass !== null && mappings[creatingClass] && (
        <CreateClassDialog
          schema={schema}
          suggested={suggestedClassName(mappings[creatingClass]?.column ?? '')}
          onCancel={() => setCreatingClass(null)}
          onCreate={(c) => {
            const column = mappings[creatingClass]?.column ?? '';
            setCreatingClass(null);
            void onCreateClass(c).then((r) => {
              if (r.ok) {
                setClassRefused((s) => {
                  const n = new Map(s);
                  n.delete(column);
                  return n;
                });
                const now = latest.current;
                const i = now.findIndex((x) => x.column === column);
                const m = now[i];
                // The class is in the ontology now; the mapping keeps its parent
                // too, so the spec declares it where the ontology does not yet.
                if (m) onChange(now.map((x, j) => (j === i ? answered({ ...m, klass: r.uri, klassParent: c.parent, klassLabel: c.label }) : x)));
              } else {
                setClassRefused((s) => new Map(s).set(column, `The class was not created: ${r.error}`));
              }
            });
          }}
        />
      )}
    </div>
  );
}
